import type { ApiClient } from './api.js';
import type { AuthBackend } from './auth.js';
import { readClaims } from './claims.js';

/**
 * ADR 242 decision 3 (plan 242 §6): one active clinic per person. The portal's tokens
 * live in `localStorage`, so every tab shares them: a switch in one tab tells the
 * others (BroadcastChannel, with a `storage` event as the fallback), and each page
 * reloads into the new clinic. The API client's tenant guard covers a missed message.
 */
export const CLINIC_CHANNEL = 'laura.session';
export const CLINIC_STORAGE_KEY = 'laura.active-clinic';

interface SwitchMessage {
  type: 'clinic-switched';
  tenant_id: string;
}

function channel(): BroadcastChannel | undefined {
  try {
    return typeof BroadcastChannel === 'function'
      ? new BroadcastChannel(CLINIC_CHANNEL)
      : undefined;
  } catch {
    return undefined;
  }
}

/** This tab's own switch: its own listener ignores it (the page reloads anyway). */
let ownSwitch: string | undefined;

/** Tells the other tabs that this person now works in `tenantId`. */
export function announceClinicSwitch(tenantId: string): void {
  ownSwitch = tenantId;
  const c = channel();
  if (c) {
    c.postMessage({ type: 'clinic-switched', tenant_id: tenantId } satisfies SwitchMessage);
    c.close();
  }
  try {
    globalThis.localStorage?.setItem(CLINIC_STORAGE_KEY, `${tenantId}:${Date.now()}`);
  } catch {
    // storage blocked: the broadcast (or the API client's guard) still tells the tabs
  }
}

/** Calls `onSwitch` when another tab switched clinic. Returns the unsubscribe. */
export function onClinicSwitch(onSwitch: (tenantId: string) => void): () => void {
  const c = channel();
  const fromChannel = (e: MessageEvent) => {
    const m = e.data as Partial<SwitchMessage> | undefined;
    if (
      m?.type === 'clinic-switched' &&
      typeof m.tenant_id === 'string' &&
      m.tenant_id !== ownSwitch
    )
      onSwitch(m.tenant_id);
  };
  const fromStorage = (e: StorageEvent) => {
    if (e.key !== CLINIC_STORAGE_KEY || !e.newValue) return;
    const [tenantId] = e.newValue.split(':');
    if (tenantId) onSwitch(tenantId);
  };
  c?.addEventListener('message', fromChannel);
  globalThis.addEventListener?.('storage', fromStorage);
  return () => {
    c?.removeEventListener('message', fromChannel);
    c?.close();
    globalThis.removeEventListener?.('storage', fromStorage);
  };
}

export class SwitchFailedError extends Error {
  override readonly name = 'SwitchFailedError';
}

/**
 * Switches to `tenantId`: the server checks the membership and moves the pointer, a
 * forced refresh runs the pre-token trigger, and the new token must be for that clinic.
 * Then the other tabs are told and the page reloads at `/` (every cache, the socket and
 * per-clinic state go with it).
 */
export async function switchClinic(input: {
  api: ApiClient;
  backend: AuthBackend;
  tenantId: string;
  go?: (url: string) => void;
}): Promise<void> {
  await input.api.post('/me/active-clinic', { tenant_id: input.tenantId });
  await enterClinic(input);
}

/** Accepts an invite (the server points the person at that clinic), then opens it. */
export async function acceptInvite(input: {
  api: ApiClient;
  backend: AuthBackend;
  tenantId: string;
  go?: (url: string) => void;
}): Promise<void> {
  await input.api.post(`/me/invites/${encodeURIComponent(input.tenantId)}/accept`);
  await enterClinic(input);
}

async function enterClinic(input: {
  backend: AuthBackend;
  tenantId: string;
  go?: (url: string) => void;
}): Promise<void> {
  const token = await input.backend.accessToken(true);
  if (!token || readClaims(token).tenantId !== input.tenantId)
    throw new SwitchFailedError('The new sign-in is not for that clinic yet');
  announceClinicSwitch(input.tenantId);
  (input.go ?? ((url: string) => globalThis.location.assign(url)))('/');
}
